import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, BehaviorSubject, tap, skip, filter } from 'rxjs';
import { EnvironmentService } from '@core/services/environment.service';
import { AddressNumberService } from './address-number.service';

export type AddressType = 'I' | 'D' | 'M';

export interface Address {
	street?: string;
	streetNumber?: string;
	additionalAddressInfo?: string;
	city?: string;
	postalCode?: string;
	countryCode?: string;
	stateCode?: string;
	addressNumber?: string;
	isShipping?: boolean;
	[key: string]: unknown;
}

export interface UserAddress {
	address?: Address;
	atype?: AddressType;
	[key: string]: unknown;
}

export interface UserSetting {
	userId: number;
	name: string;
	value: string;
}

export interface Customer {
	userId: number;
	name: string;
	surname: string;
	email: string;
	status: boolean;
	UUID: string;
	phoneNumber?: string;
	addressNumber?: string;
	businessUnit?: string;
	createdAt: Date;
	updatedAt: Date;
	usersAddresses?: UserAddress[];
	usersSettings?: UserSetting[];
}

/** Raw shape returned by the v2 customers/me endpoint */
interface CustomerV2Response {
	name: string;
	surname: string;
	email: string;
	status: boolean;
	UUID?: string;
	phoneNumber?: string;
	addressNumber?: string;
	businessUnit?: string;
	createdAt: Date;
	updatedAt: Date;
	// v2 format
	addresses?: Record<string, UserAddress[]>;
	settings?: Record<string, Record<string, string>>;
	// v1 format (kept for backward compatibility)
	usersAddresses?: UserAddress[];
	usersSettings?: UserSetting[];
}

/** Normalize a v2 API response into the internal Customer shape */
function normalizeCustomer(raw: CustomerV2Response): Customer {
	let usersAddresses: UserAddress[] | undefined = raw.usersAddresses;
	let usersSettings: UserSetting[] | undefined = raw.usersSettings;

	// Normalize v2 addresses: flatten all address-number buckets into a single array
	if (raw.addresses && !raw.usersAddresses) {
		usersAddresses = Object.entries(raw.addresses).flatMap(([addressNum, addrs]) =>
			addrs.map((addr) => ({ ...addr, addressNumber: addressNum })),
		);
	}

	// Normalize v2 settings: use the primary addressNumber bucket, convert to name/value array
	if (raw.settings && !raw.usersSettings) {
		const primaryKey = raw.addressNumber ? raw.addressNumber : Object.keys(raw.settings)[0];
		const bucket = primaryKey ? raw.settings[primaryKey] : undefined;
		if (bucket) {
			usersSettings = Object.entries(bucket).map(([name, value]) => ({
				userId: -1 as unknown as number,
				name,
				value,
			}));
		}
	}

	return {
		userId: (raw as any).userId ?? -1,
		name: raw.name,
		surname: raw.surname,
		email: raw.email,
		status: raw.status,
		UUID: raw.UUID ?? '',
		phoneNumber: raw.phoneNumber,
		addressNumber: raw.addressNumber,
		businessUnit: raw.businessUnit,
		createdAt: raw.createdAt,
		updatedAt: raw.updatedAt,
		usersAddresses,
		usersSettings,
	};
}

@Injectable({
	providedIn: 'root',
})
export class CustomerService {
	private readonly http = inject(HttpClient);
	private readonly environmentService = inject(EnvironmentService);
	private readonly addressNumberService = inject(AddressNumberService);

	// Global customer data state
	private readonly customerDataSubject = new BehaviorSubject<Customer | null>(null);
	public customerData$ = this.customerDataSubject.asObservable();

	constructor() {
		// When the active address number changes, re-fetch customer data so that
		// addresses, settings, and personal data reflect the new account context.
		this.addressNumberService.selectedAddressNumber$
			.pipe(
				skip(1), // ignore the initial BehaviorSubject value (covered by initializeCustomerData)
				filter((v): v is string => v !== null),
			)
			.subscribe(() => this.refreshCustomerData().subscribe());
	}

	/**
	 * Get current user's customer data by UUID in header
	 * @returns Observable of Customer or null if not found
	 */
	getCurrentCustomer(): Observable<Customer | null> {
		// The UUID will be attached by the AuthInterceptor automatically
		return this.http.get<CustomerV2Response>(`${this.environmentService.apiUrl}/v2/customers/me`).pipe(
			map((response) => normalizeCustomer(response)),
			tap((customer) => this.customerDataSubject.next(customer)),
			catchError((_error) => {
				this.customerDataSubject.next(null); // Clear global state on error
				return of(null);
			}),
		);
	}

	/**
	 * Get current customer data from global state (no API call)
	 * @returns Current customer data or null
	 */
	getCurrentCustomerValue(): Customer | null {
		return this.customerDataSubject.value;
	}

	/**
	 * Initialize customer data - called by AuthGuard after authentication
	 * Fetches customer data and updates global state (404 is acceptable)
	 */
	public initializeCustomerData(): void {
		this.getCurrentCustomer().subscribe({
			next: (_data) => {
				// console.log('Customer data initialized:', data);
				// 404 is acceptable - some users may not have customer records
			},
			error: (_error) => {
				// console.log('Customer data not available (this is normal for some users):', error);
				// Don't treat 404 as an error - it's expected for some users
			},
		});
	}

	/**
	 * Refresh customer data from API and update global state
	 * @returns Observable<Customer | null>
	 */
	refreshCustomerData(): Observable<Customer | null> {
		return this.getCurrentCustomer();
	}

	/**
	 * Clear customer data from global state (useful for logout)
	 */
	clearCustomerData(): void {
		this.customerDataSubject.next(null);
	}
}
